/**
 * Approximate centroids AND bounding boxes for Conakry neighborhoods.
 *
 * Detection strategy (in order):
 *   1. If the point falls INSIDE a zone bounding box → return that zone.
 *      This handles elongated zones like Kaloum (a long peninsula) reliably.
 *   2. Otherwise → return the zone whose centroid is nearest (Haversine).
 *   3. If no zone is within 15 km → return null (caller should ignore).
 *
 * Values are eyeballed from OpenStreetMap / Google Maps — precise enough at
 * neighborhood granularity for a delivery app in Conakry.
 */
export const CONAKRY_ZONES = {
  Kaloum:   { center: [9.5150, -13.7050], bbox: { minLat: 9.470, maxLat: 9.545, minLng: -13.735, maxLng: -13.680 } },
  Dixinn:   { center: [9.5500, -13.6820], bbox: { minLat: 9.545, maxLat: 9.565, minLng: -13.695, maxLng: -13.665 } },
  Matam:    { center: [9.5570, -13.6650], bbox: { minLat: 9.545, maxLat: 9.575, minLng: -13.680, maxLng: -13.645 } },
  Ratoma:   { center: [9.5876, -13.6671], bbox: { minLat: 9.575, maxLat: 9.615, minLng: -13.680, maxLng: -13.640 } },
  Taouyah:  { center: [9.5940, -13.6720], bbox: { minLat: 9.585, maxLat: 9.605, minLng: -13.685, maxLng: -13.660 } },
  Cosa:     { center: [9.5900, -13.6450], bbox: { minLat: 9.585, maxLat: 9.605, minLng: -13.660, maxLng: -13.625 } },
  "Kipé":   { center: [9.6060, -13.6440], bbox: { minLat: 9.595, maxLat: 9.618, minLng: -13.660, maxLng: -13.625 } },
  Nongo:    { center: [9.6180, -13.6150], bbox: { minLat: 9.610, maxLat: 9.635, minLng: -13.630, maxLng: -13.600 } },
  Matoto:   { center: [9.5670, -13.6100], bbox: { minLat: 9.545, maxLat: 9.590, minLng: -13.645, maxLng: -13.575 } },
  Lambanyi: { center: [9.6420, -13.5850], bbox: { minLat: 9.620, maxLat: 9.660, minLng: -13.605, maxLng: -13.560 } },
  Sonfonia: { center: [9.6600, -13.5800], bbox: { minLat: 9.640, maxLat: 9.685, minLng: -13.610, maxLng: -13.560 } },
};

const CONAKRY_BOUNDS = { minLat: 9.45, maxLat: 9.75, minLng: -13.85, maxLng: -13.45 };

// Haversine distance in km
function distanceKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const [la1, lo1] = a;
  const [la2, lo2] = b;
  const dLat = toRad(la2 - la1);
  const dLon = toRad(lo2 - lo1);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(la1)) * Math.cos(toRad(la2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function inBbox(lat, lng, b) {
  return lat >= b.minLat && lat <= b.maxLat && lng >= b.minLng && lng <= b.maxLng;
}

/**
 * Returns { zone, distanceKm, matchedBy } for the closest Conakry zone.
 *   matchedBy: "bbox" | "centroid"
 * When the point is far outside Conakry (>15km from every zone), returns null.
 */
export function nearestZone(lat, lng) {
  // 1. Bounding box hit → most reliable
  const bboxMatches = [];
  for (const [name, { center, bbox }] of Object.entries(CONAKRY_ZONES)) {
    if (inBbox(lat, lng, bbox)) {
      bboxMatches.push({ zone: name, distanceKm: distanceKm([lat, lng], center), matchedBy: "bbox" });
    }
  }
  if (bboxMatches.length === 1) return bboxMatches[0];
  if (bboxMatches.length > 1) {
    // Overlapping boxes (edge case) → pick nearest centroid among matches
    bboxMatches.sort((a, b) => a.distanceKm - b.distanceKm);
    return bboxMatches[0];
  }

  // 2. Fallback: nearest centroid
  let best = null;
  for (const [name, { center }] of Object.entries(CONAKRY_ZONES)) {
    const d = distanceKm([lat, lng], center);
    if (!best || d < best.distanceKm) best = { zone: name, distanceKm: d, matchedBy: "centroid" };
  }
  if (!best || best.distanceKm > 15) return null;
  return best;
}

export function isInConakry(lat, lng) {
  return (
    lat >= CONAKRY_BOUNDS.minLat &&
    lat <= CONAKRY_BOUNDS.maxLat &&
    lng >= CONAKRY_BOUNDS.minLng &&
    lng <= CONAKRY_BOUNDS.maxLng
  );
}
